"use client"

import { useEffect, useMemo, useRef, useState, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import Image from "next/image"
import Link from "next/link"
import {
  AlertTriangle,
  ArrowRight,
  Beaker,
  CalendarClock,
  FlaskConical,
  LayoutGrid,
  Target,
} from "lucide-react"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { Card } from "@/components/ui/card"
import { HeadlineHero, RelatedTools, ResultEmailCaptureCard, ToolPageLoadingFallback } from "@/components/tools/shared"
import { UnifiedShareButton } from "@/components/tools/shared/unified-share-button"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { useFromTool } from "@/hooks/use-from-tool"
import { analytics, contentAnalytics, runWhenPageVisible, stashEmailCaptureAttribution } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { cn } from "@/lib/utils"
import {
  TANK_MIX_PRODUCTS,
  TANK_MIX_PRODUCT_IDS,
  TANK_MIX_SURFACTANTS,
  TANK_SIZES,
} from "@/data/tank-mix-products"
import { calculateTankMix, type ApplicationMode } from "@/lib/tank-mix-calculator"

const TANK_GALLON_VALUES = TANK_SIZES.map((t) => t.gallons)

function parseProductParam(value: string | null): string {
  return value && TANK_MIX_PRODUCTS[value] ? value : "tenacity"
}

function parseTankParam(value: string | null): number {
  const n = Number(value)
  return (TANK_GALLON_VALUES as readonly number[]).includes(n) ? n : 1
}

function parseModeParam(value: string | null): ApplicationMode {
  return value === "blanket" ? "blanket" : "spot"
}

const PICKS_SURFACE = "tool-tank-mix-picks"

// Impression + click on the same surface string so PostHog can join
// affiliate_product_shown / affiliate_product_clicked and compute CTR. Extracted
// into a row component only because the impression hook must run per card.
function PickRow({
  pick,
  pickIndex,
  productId,
}: {
  pick: { name: string; note?: string; url?: string; image?: string }
  pickIndex: number
  productId: string
}) {
  // Only attribute rows that actually carry an affiliate link; the sprayer
  // fallback row has no URL, so it gets no surface and the hook no-ops.
  const impressionRef = useAffiliateImpression(pick.url ? PICKS_SURFACE : undefined, {
    article_slug: "tool:tank-mix-calculator",
    product_name: pick.name,
    affiliate_link: pick.url ?? "",
    source_context: productId,
    pick_index: pickIndex,
  })

  return (
    <div
      ref={impressionRef}
      className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-3"
    >
      <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg border border-gray-100 bg-white">
        {pick.image ? (
          <Image
            src={pick.image}
            alt={pick.name}
            width={56}
            height={56}
            className="h-full w-full object-contain p-1"
          />
        ) : (
          <FlaskConical className="h-5 w-5 text-gray-400" aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-gray-900">{pick.name}</span>
        {pick.url ? (
          <a
            href={pick.url}
            target="_blank"
            rel="noopener noreferrer nofollow sponsored"
            data-affiliate="true"
            onClick={() =>
              contentAnalytics.affiliateProductClicked(PICKS_SURFACE, {
                productName: pick.name,
                affiliateLink: pick.url ?? "",
                article_slug: "tool:tank-mix-calculator",
                source_context: productId,
                pick_index: pickIndex,
              })
            }
            className="block text-[12.5px] font-semibold text-green-600 hover:text-green-700"
          >
            Check price on Amazon →
          </a>
        ) : (
          <span className="block text-[12.5px] text-gray-500">{pick.note}</span>
        )}
      </div>
    </div>
  )
}

const SELECTED_TILE = "border-2 border-green-600 bg-green-50"
const DEFAULT_TILE = "border border-gray-200 bg-white hover:border-green-300"

function TankMixCalculatorContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("tank-mix-calculator")
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()

  const [productId, setProductId] = useState(() => parseProductParam(searchParams.get("product")))
  const [tankGallons, setTankGallons] = useState(() => parseTankParam(searchParams.get("tank")))
  const [mode, setMode] = useState<ApplicationMode>(() => parseModeParam(searchParams.get("mode")))
  const trackedUsageRef = useRef(false)

  const result = useMemo(
    () => calculateTankMix({ productId, tankGallons, mode }),
    [productId, tankGallons, mode],
  )

  // The recipe updates live; analytics fire once per session on the first
  // deliberate selection change, mirroring the live-update pattern from the
  // herbicide timing tool.
  const trackOnce = (inputs: { product: string; tank: number; mode: ApplicationMode }) => {
    if (trackedUsageRef.current) return
    trackedUsageRef.current = true
    trackUsage("tank-mix-calculator")
    analytics.calculatorUsed("tank-mix", {
      inputs: { ...inputs, restored: false },
      result: {
        product_oz: result.productOzLabel,
        surfactant: result.surfactant?.name ?? "none",
      },
      source: "live_update",
    })
  }

  // Share-link restore. The three params below fully determine the recipe, so a
  // recipient sees a finished result they never calculated and trackOnce (which
  // only runs on a selection change) would never fire — the same undercount the
  // lawn-size share path had. `restored` separates it from a fresh run, matching
  // grass_id_result_viewed. Fires once, only when a share param is actually
  // present, and only once the tab is visible: an unconditional mount fire is
  // the 2026-08-01 double-count bug, and a hidden mount is a phantom session.
  const restoreTrackedRef = useRef(false)
  useEffect(() => {
    const hasShareParams = Boolean(
      searchParams.get("product") || searchParams.get("tank") || searchParams.get("mode"),
    )
    if (!hasShareParams || restoreTrackedRef.current) return
    return runWhenPageVisible(() => {
      if (restoreTrackedRef.current) return
      restoreTrackedRef.current = true
      analytics.calculatorUsed("tank-mix", {
        inputs: { product: productId, tank: tankGallons, mode, restored: true },
        result: {
          product_oz: result.productOzLabel,
          surfactant: result.surfactant?.name ?? "none",
        },
        source: "url_params",
      })
    })
    // Restore state is read once from the URL the page mounted with; later
    // selection changes are the interaction path's job, not this one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const selectProduct = (id: string) => {
    setProductId(id)
    trackOnce({ product: id, tank: tankGallons, mode })
  }
  const selectTank = (gallons: number) => {
    setTankGallons(gallons)
    trackOnce({ product: productId, tank: gallons, mode })
  }
  const selectMode = (next: ApplicationMode) => {
    setMode(next)
    trackOnce({ product: productId, tank: tankGallons, mode: next })
  }

  const shareUrl = `/tools/tank-mix-calculator?product=${productId}&tank=${tankGallons}&mode=${mode}`

  const picks = [
    {
      name: result.product.name,
      note: result.product.activeIngredient,
      url: result.product.affiliateUrl,
      image: result.product.imageUrl,
    },
    result.surfactant && result.product.surfactant !== "none"
      ? {
          name: TANK_MIX_SURFACTANTS[result.product.surfactant].pickName,
          note: "Improves coverage and uptake",
          url: TANK_MIX_SURFACTANTS[result.product.surfactant].affiliateUrl,
          image: TANK_MIX_SURFACTANTS[result.product.surfactant].imageUrl,
        }
      : {
          name: "Battery-powered sprayer",
          note: "Consistent pressure, even coverage",
          url: undefined,
          image: undefined,
        },
  ]

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <div
        className="mx-auto max-w-6xl px-4 pt-4 pb-8 sm:px-6 md:pt-8 md:pb-16 lg:px-8"
      >
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Tank Mix Calculator" },
          ]}
        />

        <div className="mt-6">
          <HeadlineHero
            eyebrow="Rates from public product labels"
            title="How much goes in"
            accent="the tank?"
            subtitle="Pick your product and sprayer size. Get the exact amount in ounces and teaspoons, the right surfactant, and the mixing order."
          />
        </div>

        <div className="mt-9 grid items-start gap-7 lg:grid-cols-2">
          {/* Selections */}
          <Card className="rounded-3xl border-gray-200 p-6 shadow-md sm:p-7">
            <p className="mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
              1 · Your product
            </p>
            <div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label="Herbicide product">
              {TANK_MIX_PRODUCT_IDS.map((id) => {
                const p = TANK_MIX_PRODUCTS[id]
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={id === productId}
                    onClick={() => selectProduct(id)}
                    className={cn(
                      "flex min-h-16 flex-col gap-0.5 rounded-xl px-3.5 py-3 text-left transition-colors",
                      id === productId ? SELECTED_TILE : DEFAULT_TILE,
                    )}
                  >
                    <span className="text-sm font-bold text-gray-900">{p.name}</span>
                    <span className="text-xs text-gray-500">{p.activeIngredient}</span>
                  </button>
                )
              })}
            </div>

            <p className="mt-6 mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
              2 · Sprayer tank size
            </p>
            <div className="flex gap-2.5" role="radiogroup" aria-label="Tank size">
              {TANK_SIZES.map((t) => (
                <button
                  key={t.gallons}
                  type="button"
                  role="radio"
                  aria-checked={t.gallons === tankGallons}
                  onClick={() => selectTank(t.gallons)}
                  className={cn(
                    "flex min-h-[60px] flex-1 flex-col items-center gap-0.5 rounded-xl p-2.5 transition-colors",
                    t.gallons === tankGallons ? SELECTED_TILE : DEFAULT_TILE,
                  )}
                >
                  <span className="text-lg font-extrabold text-gray-900">{t.label}</span>
                  <span className="text-[11.5px] text-gray-500">{t.description}</span>
                </button>
              ))}
            </div>

            <p className="mt-6 mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
              3 · Application
            </p>
            <div className="flex gap-2.5" role="radiogroup" aria-label="Application type">
              <button
                type="button"
                role="radio"
                aria-checked={mode === "spot"}
                onClick={() => selectMode("spot")}
                className={cn(
                  "flex min-h-12 flex-1 items-center justify-center gap-2 rounded-full text-sm font-semibold text-gray-900 transition-colors",
                  mode === "spot" ? SELECTED_TILE : DEFAULT_TILE,
                )}
              >
                <Target className="h-4 w-4 text-green-600" aria-hidden="true" />
                Spot spray
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={mode === "blanket"}
                onClick={() => selectMode("blanket")}
                className={cn(
                  "flex min-h-12 flex-1 items-center justify-center gap-2 rounded-full text-sm font-semibold text-gray-900 transition-colors",
                  mode === "blanket" ? SELECTED_TILE : DEFAULT_TILE,
                )}
              >
                <LayoutGrid className="h-4 w-4 text-green-600" aria-hidden="true" />
                Blanket app
              </button>
            </div>
            <p className="mt-3.5 text-[13px] leading-normal text-gray-500">
              Spot rates are stronger per gallon. Blanket assumes 1 gallon of mix covers 1,000 sq ft.
            </p>
          </Card>

          {/* Mixing recipe */}
          <div className="overflow-hidden rounded-3xl border border-green-100 bg-white shadow-md">
            <div className="flex items-center justify-between gap-3 bg-linear-to-r from-green-600 to-emerald-600 px-6 py-4 text-white">
              <span className="flex items-center gap-2.5 text-base font-bold">
                <Beaker className="h-5 w-5" aria-hidden="true" />
                Your mixing recipe
              </span>
              <span className="flex items-center gap-2">
                <span className="hidden text-[13px] font-semibold opacity-90 sm:inline">{result.recipeTitle}</span>
                <UnifiedShareButton
                  url={shareUrl}
                  title="My tank mix recipe"
                  text={`${result.product.name}: ${result.productOzLabel} fl oz (${result.productTspLabel} tsp) in ${result.tankGallons} gal of water`}
                  variant="hero-overlay"
                />
              </span>
            </div>

            <div className="p-6" aria-live="polite">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-2xl border border-green-200 bg-green-50 p-4 text-center">
                  <div className="font-editorial text-2xl font-extrabold leading-none text-green-900">
                    {result.productOzLabel}
                  </div>
                  <div className="mt-1.5 text-xs font-semibold text-green-800">fl oz product</div>
                </div>
                <div className="rounded-2xl border border-green-200 bg-green-50 p-4 text-center">
                  <div className="font-editorial text-2xl font-extrabold leading-none text-green-900">
                    {result.productTspLabel}
                  </div>
                  <div className="mt-1.5 text-xs font-semibold text-green-800">teaspoons</div>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-center">
                  <div className="font-editorial text-2xl font-extrabold leading-none text-gray-900">
                    {result.tankGallons}
                  </div>
                  <div className="mt-1.5 text-xs font-semibold text-gray-500">gal water</div>
                </div>
              </div>

              <p className="mt-5 mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
                Mixing order
              </p>
              <ol className="flex list-none flex-col gap-2.5 p-0">
                {result.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-green-600 text-sm font-extrabold text-white">
                      {i + 1}
                    </span>
                    <p className="mt-0.5 text-[15px] leading-normal text-gray-700">{step}</p>
                  </li>
                ))}
              </ol>

              <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
                <AlertTriangle className="mt-0.5 h-[18px] w-[18px] shrink-0 text-amber-600" aria-hidden="true" />
                <p className="text-[13.5px] leading-relaxed text-amber-900">
                  {result.caution} Always verify rates against your product label. Label is the law.
                </p>
              </div>

              <p className="mt-5 mb-2.5 text-[13px] font-bold uppercase tracking-wider text-gray-500">
                What you&apos;ll need
              </p>
              <div className="flex flex-col gap-2">
                {picks.map((pick, i) => (
                  <PickRow key={pick.name} pick={pick} pickIndex={i} productId={productId} />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <ResultEmailCaptureCard
            source="tool_result_tank_mix"
            toolDisplayName="tank mix"
            subcopy="Keep this tank mix handy and track your lawn over time."
            accountLead={{
              redirectTo: shareUrl,
              headline: "Save my tank mix",
              subcopy: "Keep this tank mix in a free account and track your lawn over time.",
              onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_tank_mix"),
            }}
            successSubcopy="Your lawn tips are on the way. Your tank mix stays right here to revisit anytime."
            onCaptured={() => closeNewsletterModal()}
          />
        </div>

        {/* Timing cross-link */}
        <div className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl border border-gray-200 bg-white px-6 py-4.5">
          <CalendarClock className="h-[22px] w-[22px] shrink-0 text-green-600" aria-hidden="true" />
          <p className="min-w-60 flex-1 text-[14.5px] text-gray-700">
            <strong className="text-gray-900">Timing matters as much as rate.</strong> Check when to
            spray with the Herbicide Timing tool.
          </p>
          <Link
            href="/tools/herbicide-timing"
            className="flex min-h-11 items-center gap-2 whitespace-nowrap rounded-full border border-green-200 bg-green-50 px-5 text-sm font-bold text-green-800 transition-transform hover:-translate-y-px"
          >
            Check my timing
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        {/* Empty prefill: this tool has no ZIP/grass/size inputs, so suppress
            the saved-input "carries over" chips that would otherwise render. */}
        <RelatedTools currentToolId="tank-mix-calculator" prefillValues={{}} className="mt-10" />
      </div>

      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Tank Mix Calculator"
        usageCount={usageCount}
      />
    </div>
  )
}

export function TankMixCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="tank-mix-calculator" />}>
      <SearchParamsHydrationGate>
        <TankMixCalculatorContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
